/**
 * PAPPO — ChatPage
 * Lista de conversas (esquerda) + janela de chat (direita), em tempo real.
 * Usa a API REST para conversas/histórico e Socket.IO para mensagens ao vivo.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import type { Conversation, Message } from '../types/message';
import { listConversations, getMessages } from '../services/conversations';
import { updateFamily } from '../services/family';
import { updateGroup } from '../services/groups';
import { uploadImage } from '../services/uploads';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { useNotifications } from '../contexts/NotificationsContext';
import { ConversationList } from '../components/chat/ConversationList';
import { MoodCheckIn } from '../components/chat/MoodCheckIn';
import { ChatWindow } from '../components/chat/ChatWindow';
import { NewGroupModal } from '../components/chat/NewGroupModal';
import { GroupManageModal } from '../components/chat/GroupManageModal';
import { NoticeModal } from '../components/common/NoticeModal';
import { MessageCircle, Plus, UsersRound } from 'lucide-react';
import styles from './ChatPage.module.css';

/** Texto curto para a lista de conversas (imagem/figurinha não têm texto próprio) */
function previewOf(msg: Message): string {
  if (msg.message_type === 'sticker') return 'Figurinha';
  if (msg.message_type === 'image') return msg.content || '📷 Imagem';
  if (msg.message_type === 'audio') return msg.content || '🎤 Áudio';
  if (msg.message_type === 'video') return msg.content || '🎬 Vídeo';
  return msg.content;
}

export function ChatPage() {
  const { user, logout } = useAuth();
  const { emit, on } = useSocket();
  const { refresh: refreshNotifications, setActiveConversation } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConv, setActiveConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Record<number, Message[]>>({});
  /* IDs de utilizadores atualmente online (presença em tempo real) */
  const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
  /* Nome de quem está "a escrever..." por conversa (1x1 → um nome) */
  const [typingByConv, setTypingByConv] = useState<Record<number, string>>({});
  /* Grupos de utilizador: modal de criação e grupo em gestão (adicionar/sair) */
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [managingGroup, setManagingGroup] = useState<Conversation | null>(null);
  /* Aviso de erro do servidor no envio (bloqueio, nível 00, não-verificado…) */
  const [sendError, setSendError] = useState<string | null>(null);

  /* Refs para uso dentro de handlers do socket (evita closures obsoletas) */
  const activeConvRef = useRef<Conversation | null>(null);
  activeConvRef.current = activeConv;
  const joinedRef = useRef<Set<number>>(new Set());
  const autoOpenedRef = useRef(false);
  /* Garante que a restauração da conversa aberta (pós-reload) corre uma só vez */
  const restoredRef = useRef(false);

  const currentUserId = user?.id ?? 0;
  /* Adulto Nível 00 não-verificado: pode ver o chat, mas não conversar. */
  const precisaVerificarChat =
    !!user &&
    user.role !== 'child' &&
    !(user.email_verified && user.phone_verified);

  /* Garante que o socket entrou na sala da conversa (uma só vez) */
  const joinConversation = useCallback(
    (conversationId: number) => {
      if (!joinedRef.current.has(conversationId)) {
        const token = localStorage.getItem('pappo_access_token');
        emit('join_conversation', { token, conversation_id: conversationId });
        joinedRef.current.add(conversationId);
      }
    },
    [emit]
  );

  /* Carrega as conversas e entra nas respetivas salas do socket */
  const loadConversations = useCallback(async () => {
    const res = await listConversations();
    if (res.success && res.data) {
      setConversations(res.data.conversations);
      res.data.conversations.forEach((c) => joinConversation(c.id));
      return res.data.conversations;
    }
    return [];
  }, [joinConversation]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  /* Abre uma conversa: carrega histórico (se ainda não em cache) e zera não-lidas */
  const handleSelect = useCallback(
    async (conv: Conversation) => {
      setActiveConv(conv);
      setActiveConversation(conv.id);
      /* Persiste a conversa aberta p/ sobreviver a um reload da mesma sessão
         (o webview do celular recarrega ao voltar da câmera; também vale p/ F5) */
      sessionStorage.setItem('pappo_active_conv', String(conv.id));
      joinConversation(conv.id);
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c))
      );

      if (!messages[conv.id]) {
        const res = await getMessages(conv.id);
        if (res.success && res.data) {
          setMessages((prev) => ({ ...prev, [conv.id]: res.data!.messages }));
        }
      }

      /* Recibo de leitura: marca recebidas como lidas e avisa o remetente */
      const token = localStorage.getItem('pappo_access_token');
      emit('mark_read', { token, conversation_id: conv.id });

      /* Ressincroniza o contador global após ler a conversa */
      refreshNotifications();
    },
    [messages, emit, joinConversation, refreshNotifications, setActiveConversation]
  );

  /* Listener global de mensagens recebidas (tempo real) */
  useEffect(() => {
    const off = on('receive_message', (...args) => {
      const msg = args[0] as Message;

      setMessages((prev) => {
        const list = prev[msg.conversation_id];
        if (!list) return prev; // histórico ainda não carregado — virá ao abrir
        if (list.some((m) => m.id === msg.id)) return prev; // evita duplicados
        return { ...prev, [msg.conversation_id]: [...list, msg] };
      });

      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === msg.conversation_id);
        if (idx === -1) {
          loadConversations(); // conversa nova — recarrega lista
          return prev;
        }
        const updated: Conversation = {
          ...prev[idx],
          last_message: {
            content: previewOf(msg),
            created_at: msg.created_at,
            sender_id: msg.sender_id,
          },
        };
        if (
          activeConvRef.current?.id !== msg.conversation_id &&
          msg.sender_id !== currentUserId
        ) {
          updated.unread_count = (updated.unread_count ?? 0) + 1;
        }
        const rest = prev.filter((_, i) => i !== idx);
        return [updated, ...rest]; // move para o topo
      });

      /* Se a conversa está aberta e a mensagem é do outro, marca lida na hora */
      if (
        activeConvRef.current?.id === msg.conversation_id &&
        msg.sender_id !== currentUserId
      ) {
        const token = localStorage.getItem('pappo_access_token');
        emit('mark_read', { token, conversation_id: msg.conversation_id });
      }
    });
    return off;
  }, [on, loadConversations, currentUserId, emit]);

  /* Conversa nova empurrada pelo servidor (sem refresh da página) */
  useEffect(() => {
    const off = on('conversation:new', (...args) => {
      const conv = args[0] as Conversation;
      setConversations((prev) => {
        if (prev.some((c) => c.id === conv.id)) return prev; // já existe
        return [conv, ...prev];
      });
      joinConversation(conv.id);
    });
    return off;
  }, [on, joinConversation]);

  /* Grupo família: nome/foto trocados por um adulto — atualiza para todos ao vivo */
  useEffect(() => {
    const off = on('family:update', (...args) => {
      const { id, name, avatar_url } = args[0] as {
        id: number;
        name: string;
        avatar_url: string | null;
      };
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, name, avatar_url } : c))
      );
      setActiveConv((prev) =>
        prev && prev.id === id ? { ...prev, name, avatar_url } : prev
      );
    });
    return off;
  }, [on]);

  /* Erros vindos do servidor no envio (recusa: bloqueio, nível 00, não-verificado).
     Sem escutar isto, a mensagem falhava em silêncio e parecia "app quebrado". */
  useEffect(() => {
    const off = on('error', (...args) => {
      const msg = (args[0] as { message?: string } | undefined)?.message;
      if (msg) setSendError(msg);
    });
    return off;
  }, [on]);
  /* O aviso agora é um pop-up (NoticeModal) que o usuário fecha no "OK" — sem
     auto-dismiss. Exceção: account:blocked encerra a sessão sozinho (abaixo). */

  /* Moderação (F3): conta suspensa/banida derrubada no connect do socket →
     avisa e encerra a sessão (o login/refresh também já barram novas sessões). */
  useEffect(() => {
    const off = on('account:blocked', (...args) => {
      const msg = (args[0] as { message?: string } | undefined)?.message;
      setSendError(msg || 'Sua conta foi bloqueada pela moderação.');
      const t = setTimeout(() => logout(), 2000);
      return () => clearTimeout(t);
    });
    return off;
  }, [on, logout]);

  /* Presença online/offline dos contactos */
  useEffect(() => {
    const off = on('presence:update', (...args) => {
      const { user_id, status } = args[0] as {
        user_id: number;
        status: 'online' | 'offline';
      };
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (status === 'online') next.add(user_id);
        else next.delete(user_id);
        return next;
      });
    });
    return off;
  }, [on]);

  /* Indicador "a escrever..." */
  useEffect(() => {
    const offStart = on('typing:start', (...args) => {
      const { conversation_id, display_name } = args[0] as {
        conversation_id: number;
        user_id: number;
        display_name?: string;
      };
      setTypingByConv((prev) => ({
        ...prev,
        [conversation_id]: display_name || 'Alguém',
      }));
    });
    const offStop = on('typing:stop', (...args) => {
      const { conversation_id } = args[0] as { conversation_id: number };
      setTypingByConv((prev) => {
        if (!(conversation_id in prev)) return prev;
        const next = { ...prev };
        delete next[conversation_id];
        return next;
      });
    });
    return () => {
      offStart();
      offStop();
    };
  }, [on]);

  /* Recibo de leitura: o outro leu — marca as minhas mensagens como lidas */
  useEffect(() => {
    const off = on('message:read', (...args) => {
      const { conversation_id, reader_id } = args[0] as {
        conversation_id: number;
        reader_id: number;
      };
      setMessages((prev) => {
        const list = prev[conversation_id];
        if (!list) return prev;
        let changed = false;
        const updated = list.map((m) => {
          if (m.sender_id !== reader_id && !m.is_read) {
            changed = true;
            return { ...m, is_read: true };
          }
          return m;
        });
        return changed ? { ...prev, [conversation_id]: updated } : prev;
      });
    });
    return off;
  }, [on]);

  /* Reações em tempo real: mescla o estado agregado na mensagem por id */
  useEffect(() => {
    const off = on('message:reaction', (...args) => {
      const { message_id, conversation_id, reactions } = args[0] as {
        message_id: number;
        conversation_id: number;
        reactions: Message['reactions'];
      };
      setMessages((prev) => {
        const list = prev[conversation_id];
        if (!list) return prev;
        const idx = list.findIndex((m) => m.id === message_id);
        if (idx === -1) return prev;
        const updated = [...list];
        updated[idx] = { ...updated[idx], reactions };
        return { ...prev, [conversation_id]: updated };
      });
    });
    return off;
  }, [on]);

  /* Restaura a conversa aberta após um reload da MESMA sessão (câmera no mobile / F5).
     Como a conversa ativa vive só em estado e a rota /chat não tem id, qualquer reload
     cairia na lista; aqui relemos o id salvo e reabrimos pelo MESMO handler (entra na
     sala do socket, carrega mensagens, marca lida — não só setActiveConv). Uma vez só. */
  useEffect(() => {
    if (restoredRef.current || !conversations.length) return;
    /* Abertura explícita vinda de /contacts tem prioridade — deixa o auto-open agir */
    if ((location.state as { openConversationId?: number } | null)?.openConversationId) {
      restoredRef.current = true;
      return;
    }
    const savedId = sessionStorage.getItem('pappo_active_conv');
    if (!savedId) {
      restoredRef.current = true;
      return;
    }
    const conv = conversations.find((c) => c.id === Number(savedId));
    if (conv) {
      handleSelect(conv);
    } else {
      sessionStorage.removeItem('pappo_active_conv'); // conversa já não existe
    }
    restoredRef.current = true;
  }, [conversations, location.state, handleSelect]);

  /* Abre automaticamente a conversa indicada ao vir da página de contactos */
  useEffect(() => {
    const openId = (location.state as { openConversationId?: number } | null)
      ?.openConversationId;
    if (openId && !autoOpenedRef.current && conversations.length) {
      const conv = conversations.find((c) => c.id === openId);
      if (conv) {
        autoOpenedRef.current = true;
        handleSelect(conv);
      }
    }
  }, [location.state, conversations, handleSelect]);

  /* Envia mensagem via socket (o servidor devolve via receive_message) */
  const handleSendMessage = useCallback(
    (content: string, replyToId?: number | null) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('send_message', {
        token,
        conversation_id: activeConv.id,
        content,
        message_type: 'text',
        reply_to_id: replyToId ?? null,
      });
    },
    [activeConv, emit]
  );

  /* Encaminha uma mensagem para outra conversa: reenvia conteúdo/anexo com a
     marca is_forwarded. O anexo vai SEM a assinatura (?exp&sig) — o backend só
     aceita o caminho interno /uploads/<id> e re-assina na saída. */
  const handleForwardMessage = useCallback(
    async (message: Message, targetConversationId: number) => {
      /* Abre o destino PRIMEIRO (carrega o histórico + entra na sala) para que o
         eco do socket da mensagem encaminhada seja anexado ao vivo — senão a
         navegação carregaria o snapshot REST antes de a mensagem existir. */
      const target = conversations.find((c) => c.id === targetConversationId);
      if (target) await handleSelect(target);
      const token = localStorage.getItem('pappo_access_token');
      const attachment = message.attachment_url
        ? message.attachment_url.split('?')[0]
        : undefined;
      emit('send_message', {
        token,
        conversation_id: targetConversationId,
        content: message.content ?? '',
        message_type: message.message_type,
        attachment_url: attachment,
        is_forwarded: true,
      });
    },
    [emit, conversations, handleSelect]
  );

  /* Envia imagem (legenda opcional) — servidor ecoa via receive_message */
  const handleSendImage = useCallback(
    (url: string, caption: string) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('send_message', {
        token,
        conversation_id: activeConv.id,
        content: caption,
        message_type: 'image',
        attachment_url: url,
      });
    },
    [activeConv, emit]
  );

  /* Envia nota de voz — o servidor transcreve e ecoa via receive_message
     (content = transcrição). content vazio aqui; o backend preenche. */
  const handleSendAudio = useCallback(
    (url: string) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('send_message', {
        token,
        conversation_id: activeConv.id,
        content: '',
        message_type: 'audio',
        attachment_url: url,
      });
    },
    [activeConv, emit]
  );

  /* Envia vídeo (gravado no celular ou escolhido no PC) com legenda opcional —
     servidor ecoa via receive_message. Supervisão é por visualização; a legenda,
     sendo texto, ainda passa pela IA quando o remetente é criança. */
  const handleSendVideo = useCallback(
    (url: string, caption: string) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('send_message', {
        token,
        conversation_id: activeConv.id,
        content: caption,
        message_type: 'video',
        attachment_url: url,
      });
    },
    [activeConv, emit]
  );

  /* Envia figurinha (já rehospedada em /uploads) — servidor ecoa via receive_message */
  const handleSendSticker = useCallback(
    (url: string) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('send_message', {
        token,
        conversation_id: activeConv.id,
        content: '',
        message_type: 'sticker',
        attachment_url: url,
      });
    },
    [activeConv, emit]
  );

  /* Alterna uma reação — servidor devolve o estado via message:reaction */
  const handleReact = useCallback(
    (messageId: number, emoji: string) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit('message:react', {
        token,
        conversation_id: activeConv.id,
        message_id: messageId,
        emoji,
      });
    },
    [activeConv, emit]
  );

  /* Emite typing:start / typing:stop para a conversa ativa (debounce no input) */
  const handleTyping = useCallback(
    (isTyping: boolean) => {
      if (!activeConv) return;
      const token = localStorage.getItem('pappo_access_token');
      emit(isTyping ? 'typing:start' : 'typing:stop', {
        token,
        conversation_id: activeConv.id,
      });
    },
    [activeConv, emit]
  );

  const handleBack = useCallback(() => {
    setActiveConv(null);
    setActiveConversation(null);
    /* Voltou à lista de propósito: não há conversa a restaurar num próximo reload */
    sessionStorage.removeItem('pappo_active_conv');
  }, [setActiveConversation]);

  /* Grupo família: aplica o grupo atualizado (nome/foto/participantes) no estado */
  const applyFamilyUpdate = useCallback((fam: Conversation) => {
    setConversations((prev) => prev.map((c) => (c.id === fam.id ? fam : c)));
    setActiveConv((prev) => (prev && prev.id === fam.id ? fam : prev));
  }, []);

  /* Renomeia o grupo. Grupo de utilizador → /groups (admin); Família → /family (adulto) */
  const handleRenameGroup = useCallback(
    async (name: string) => {
      if (!activeConv?.is_group) return;
      if (activeConv.group_kind === 'user') {
        const res = await updateGroup(activeConv.id, { name });
        if (res.success && res.data?.group) applyFamilyUpdate(res.data.group);
      } else {
        const res = await updateFamily(activeConv.id, { name });
        if (res.success && res.data?.family) applyFamilyUpdate(res.data.family);
      }
    },
    [activeConv, applyFamilyUpdate]
  );

  /* Troca a foto do grupo: POST /uploads e depois PUT no endpoint certo */
  const handleChangeGroupPhoto = useCallback(
    async (file: File) => {
      if (!activeConv?.is_group) return;
      const up = await uploadImage(file);
      if (!up.success || !up.data) return;
      if (activeConv.group_kind === 'user') {
        const res = await updateGroup(activeConv.id, {
          avatar_url: up.data.attachment_url,
        });
        if (res.success && res.data?.group) applyFamilyUpdate(res.data.group);
      } else {
        const res = await updateFamily(activeConv.id, {
          avatar_url: up.data.attachment_url,
        });
        if (res.success && res.data?.family) applyFamilyUpdate(res.data.family);
      }
    },
    [activeConv, applyFamilyUpdate]
  );

  /* Grupo de utilizador criado: adiciona à lista, entra na sala e abre */
  const handleGroupCreated = useCallback(
    (group: Conversation) => {
      setConversations((prev) =>
        prev.some((c) => c.id === group.id)
          ? prev.map((c) => (c.id === group.id ? group : c))
          : [group, ...prev]
      );
      joinConversation(group.id);
      handleSelect(group);
    },
    [joinConversation, handleSelect]
  );

  /* Grupo de utilizador alterado (membros): reflete na lista/ativa/gestão */
  const handleGroupChanged = useCallback((group: Conversation) => {
    setConversations((prev) => prev.map((c) => (c.id === group.id ? group : c)));
    setActiveConv((prev) => (prev && prev.id === group.id ? group : prev));
    setManagingGroup((prev) => (prev && prev.id === group.id ? group : prev));
  }, []);

  /* O utilizador saiu de um grupo: remove-o da lista (e fecha se estava aberto) */
  const handleGroupLeft = useCallback(
    (groupId: number) => {
      setConversations((prev) => prev.filter((c) => c.id !== groupId));
      setManagingGroup(null);
      if (activeConvRef.current?.id === groupId) {
        setActiveConv(null);
        setActiveConversation(null);
        sessionStorage.removeItem('pappo_active_conv');
      }
    },
    [setActiveConversation]
  );

  const activeMessages = activeConv ? messages[activeConv.id] ?? [] : [];
  const activeOtherId = activeConv?.other_user?.id;
  const activeIsOnline = activeOtherId
    ? onlineUserIds.has(activeOtherId)
    : false;
  /* Mostra "a escrever" apenas se for o OUTRO participante (não eu) */
  const activeTypingName =
    activeConv && typingByConv[activeConv.id] ? typingByConv[activeConv.id] : null;

  return (
    <>
      {sendError && (
        <NoticeModal message={sendError} onClose={() => setSendError(null)} />
      )}
      {precisaVerificarChat && (
        <Link
          to="/verificacao"
          style={{
            display: 'block',
            padding: '0.6rem 1rem',
            textAlign: 'center',
            background: 'var(--accent-glow)',
            borderBottom: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontSize: '0.85rem',
            textDecoration: 'none',
          }}
        >
          Verifique seu email e telefone para poder conversar →
        </Link>
      )}
      <div className={styles.page}>
        {/* Sidebar — conversation list */}
      <aside className={`${styles.sidebar} ${activeConv ? styles.hideMobile : ''}`}>
        <div
          className={styles.sidebarHeader}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h2 className={styles.sidebarTitle}>Conversas</h2>
          <button
            onClick={() => setShowNewGroup(true)}
            aria-label="Novo grupo"
            title="Novo grupo"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              border: '1px solid var(--border-color)',
              background: 'transparent',
              color: 'var(--text-primary)',
              borderRadius: 'var(--pappo-radius-md, 8px)',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            <UsersRound size={16} /> Novo grupo
          </button>
        </div>
        {user?.role === 'child' && <MoodCheckIn />}
        <ConversationList
          conversations={conversations}
          activeId={activeConv?.id}
          onSelect={handleSelect}
          onlineUserIds={onlineUserIds}
          onManageGroup={setManagingGroup}
        />
      </aside>

      {/* Main — chat window */}
      <main className={`${styles.main} ${!activeConv ? styles.hideMobile : ''}`}>
        {activeConv ? (
          <ChatWindow
            key={activeConv.id}
            conversation={activeConv}
            messages={activeMessages}
            currentUserId={currentUserId}
            onSendMessage={handleSendMessage}
            onSendImage={handleSendImage}
            onSendSticker={handleSendSticker}
            onSendAudio={handleSendAudio}
            onSendVideo={handleSendVideo}
            onReact={handleReact}
            onBack={handleBack}
            isOnline={activeIsOnline}
            typingName={activeTypingName}
            onTyping={handleTyping}
            canEditGroup={user?.role !== 'child'}
            onRenameGroup={handleRenameGroup}
            onChangeGroupPhoto={handleChangeGroupPhoto}
            conversations={conversations}
            onForwardMessage={handleForwardMessage}
          />
        ) : conversations.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>Comece uma conversa</p>
            <button
              className={styles.emptyAddBtn}
              onClick={() => navigate('/contacts')}
              aria-label="Adicionar contacto"
            >
              <Plus size={22} />
            </button>
          </div>
        ) : (
          <div className={styles.placeholder}>
            <MessageCircle size={48} className={styles.placeholderIcon} />
            <h3>Selecione uma conversa</h3>
            <p>Escolha um contacto à esquerda para começar a conversar</p>
          </div>
        )}
      </main>
      </div>

      {showNewGroup && (
        <NewGroupModal
          onClose={() => setShowNewGroup(false)}
          onCreated={handleGroupCreated}
        />
      )}

      {managingGroup && (
        <GroupManageModal
          group={managingGroup}
          currentUserId={currentUserId}
          onClose={() => setManagingGroup(null)}
          onChanged={handleGroupChanged}
          onLeft={handleGroupLeft}
        />
      )}
    </>
  );
}
